import { loadSupersetFilters } from '../charts/superset-bundle.js';

/**
 * The Alpine half of <x-filters.select> (App\View\Components\Filters\Select): Superset's own Select
 * filter from the blink-superset bundle, bound to a Livewire property holding a list of values
 * (null when cleared) through x-modelable.
 *
 * sf_value IS the bound property: Livewire moves it when the server does, and the host moves it when
 * the reader picks. The host sends values only, never the DataMask's extraFormData, which is
 * Superset's query clause for Superset's backend: the server builds its own, with the values bound.
 * blink-superset-bundle.md, "The x-filters hosts", says why each rule below exists.
 */

// The component keys its rows by a column name and puts it in its query clause, which the host never
// sends; the column a report filters is the server's, named in the filter itself.
const COLUMN = 'value';

const COMBOBOX = 'input[role="combobox"]';

const isNonEmptyString = (value) => typeof value === 'string' && value !== '';

/** The bound value as the adapter takes it: a non-empty list of strings, or null. */
function toSelection(value) {
    if (value === null || value === undefined) return { value: null, readable: true };
    if (Array.isArray(value) && value.every(isNonEmptyString)) return { value: value.length ? [...value] : null, readable: true };
    return { value: null, readable: false };
}

/** The reader's selection in a mask: its strings, or null for a clear. */
function fromDataMask(dataMask) {
    const value = dataMask?.filterState?.value;
    const strings = Array.isArray(value) ? value.filter(isNonEmptyString) : [];
    return strings.length ? strings : null;
}

function sameSelection(a, b) {
    const key = (value) => (Array.isArray(value) && value.length ? JSON.stringify(value) : null);
    return key(a) === key(b);
}

/**
 * The options the server rendered: each a value, or a value and the label it reads as (a country's
 * name for its code). Read inside sf_mount()'s try, so a script that does not parse shows the failed
 * state and logs, rather than a filter with nothing in it.
 */
function readOptions(root) {
    const options = JSON.parse(root.querySelector('script[data-select-filter-options]')?.textContent || '[]');
    return Array.isArray(options) ? options : [];
}

/** Each labelled option's label, keyed by its value. */
function labelsOf(options) {
    return new Map(options
        .filter((option) => isNonEmptyString(option?.value) && isNonEmptyString(option?.label))
        .map((option) => [option.value, option.label]));
}

function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

export default function SelectFilter(config = {}) {
    const ids = config.ids ?? {};
    const text = config.text ?? {};

    // In the closure, not reactive: Alpine would deep-proxy the bundle and the React root it drives.
    let bundle = null;
    let observers = [];
    let listeners = [];
    let alive = true;
    let mounted = false;
    // The value this host last wrote, until $watch hands it back.
    let sent;
    let hasSent = false;
    // The selection the combobox holds, so a $watch for it is not taken for a change.
    let mountedFor = null;
    // Whether the option list was open when an Escape keydown began.
    let escapeClosesList = false;

    const isListOpen = (mount) => mount.querySelector(`${COMBOBOX}[aria-expanded="true"]`) !== null;

    return {
        sf_value: null,
        // The labels of the options last mounted, so the combobox's name says what the list shows.
        sf_labels: new Map(),
        sf_state: 'loading',
        sf_announcement: '',
        sf_text: {
            loadFailed: text.loadFailed ?? 'This filter could not be loaded.',
            retry: text.retry ?? 'Retry',
        },

        init() {
            // Two hosts with one id share their label, hint and error ids, so each would name the
            // other's combobox.
            const twins = document.querySelectorAll(`[data-select-filter="${config.id}"]`);
            if (twins.length > 1 && twins[0] !== this.$root) {
                console.error('Select filter id is not unique on the page: pass a distinct id', { id: config.id });
            }

            this.$watch('sf_value', (value) => this.sf_follow(value));

            // Both are server-rendered on the morphable root, while the combobox they describe is
            // React's: a validation error, and the options after a render.
            const invalid = new MutationObserver(() => this.sf_mount(this.sf_value));
            invalid.observe(this.$root, { attributes: true, attributeFilter: ['data-invalid'] });
            observers = [invalid];

            const options = this.$root.querySelector('script[data-select-filter-options]');
            if (options) {
                const moved = new MutationObserver(() => this.sf_mount(this.sf_value));
                moved.observe(options, { childList: true, characterData: true, subtree: true });
                observers.push(moved);
            }

            // The name and description are attributes React does not manage on the input it owns, so
            // they are put back whenever it renders a new one.
            const rendered = new MutationObserver(() => this.sf_wire());
            rendered.observe(this.$refs.mount, { childList: true, subtree: true });
            observers.push(rendered);

            // Two keys antd answers unlike the rest of the page, both while the list is open:
            //  - Tab is antd's Enter: it toggles the highlighted value and keeps focus, so a reader
            //    pressing Tab to leave changes the filter instead (rc-select's OptionList, measured in
            //    Chrome). Stopped here, in capture on the root and so before React's listener on the
            //    mount sees it, Tab moves focus on as it does everywhere else (the list's own buttons
            //    are out of the tab order, see sf_wire()), and antd closes the list as the input blurs.
            //  - Escape closes the list and goes on to window, where an enclosing <x-modal> would
            //    close too. Read before antd handles it (capture), stopped after (bubble).
            const beforeKey = (event) => {
                const open = isListOpen(this.$refs.mount);
                escapeClosesList = event.key === 'Escape' && open;
                if (event.key === 'Tab' && open) event.stopPropagation();
            };
            const afterKey = (event) => {
                if (event.key === 'Escape' && escapeClosesList) event.stopPropagation();
                escapeClosesList = false;
            };
            this.$root.addEventListener('keydown', beforeKey, true);
            this.$root.addEventListener('keydown', afterKey);
            listeners = [['keydown', beforeKey, true], ['keydown', afterKey, false]];

            // A microtask later: x-modelable hands over the bound value in a microtask it queues
            // after init(), and a chunk already fetched would otherwise mount an empty filter first.
            queueMicrotask(() => this.sf_load());
        },

        destroy() {
            alive = false;
            observers.forEach((observer) => observer.disconnect());
            listeners.forEach(([type, listener, capture]) => this.$root.removeEventListener(type, listener, capture));
            if (bundle && mounted) this.sf_unmount();
            bundle = null;
        },

        sf_load({ refocus = false } = {}) {
            if (! alive) return;
            this.sf_state = 'loading';

            loadSupersetFilters()
                .then((loaded) => {
                    if (! alive) return;
                    bundle = loaded;
                    this.sf_state = 'ready';
                    if (! this.sf_mount(this.sf_value)) return;
                    // A recovered filter must not leave "could not be loaded" behind in the alert.
                    this.sf_announcement = '';
                    if (refocus) this.$refs.mount.querySelector(COMBOBOX)?.focus();
                })
                .catch((error) => this.sf_fail(error, refocus));
        },

        /** The current values as the text the combobox's name ends with, each by its label. */
        sf_selectionText() {
            return toSelection(this.sf_value).value?.map((value) => this.sf_labels.get(value) ?? value).join(', ') ?? '';
        },

        sf_retry() {
            this.sf_load({ refocus: true });
        },

        sf_fail(error, refocus = false) {
            console.error('Select filter failed to load', { id: config.id, error });
            bundle = null;
            mounted = false;
            this.sf_state = 'failed';
            // Cleared first and set on the next frame, so a second failure in a row is spoken again.
            this.sf_announcement = '';
            requestAnimationFrame(() => { this.sf_announcement = this.sf_text.loadFailed; });
            // Pressing Retry hid the button; a failure brings it back, and focus with it.
            if (refocus) this.$nextTick(() => this.$refs.retry?.focus());
        },

        sf_unmount() {
            bundle.unmountSelectFilter(this.$refs.mount);
            mounted = false;
        },

        /** Mounts, or re-renders in place, for a bound value. Returns whether the filter is up. */
        sf_mount(value) {
            if (! bundle) return false;

            const selection = toSelection(value);
            if (! selection.readable) console.error('Select filter value is not a list it can show', { id: config.id, value });

            const invalid = this.$root.dataset.invalid === 'true';

            try {
                const options = readOptions(this.$root);
                this.sf_labels = labelsOf(options);
                bundle.mountSelectFilter(this.$refs.mount, {
                    id: config.id,
                    column: COLUMN,
                    options,
                    // Never creatable: a value the column does not hold would narrow every card to
                    // nothing, and the server would refuse it anyway.
                    formData: { multiSelect: config.multiple !== false, creatable: false },
                    filterState: { value: selection.value, validateStatus: invalid ? 'error' : undefined },
                    onDataMask: (dataMask, change) => this.sf_receive(dataMask, change),
                });
            } catch (error) {
                // The adapter registers its instance before it renders; left behind, Retry would
                // re-render it in place.
                this.sf_unmount();
                this.sf_fail(error);
                return false;
            }

            mounted = true;
            mountedFor = selection.value;
            this.sf_wire(invalid);

            return true;
        },

        /**
         * Names the combobox by the visible label and the current values (Superset's Select labels
         * it with its own id, and a single select with "id: value") and describes it with the
         * instructions, hint and error; and marks it invalid while the server says so, which antd's
         * input cannot learn from Blade.
         *
         * The open list's Select all and Clear buttons are taken out of the tab order: Tab from the
         * combobox would land on one, antd then closes the list as the input blurs, and focus falls
         * to <body> (measured in Chrome). Control+A and Control+Shift+A, which the instructions name,
         * do the same from the combobox.
         */
        sf_wire(invalid = this.$root.dataset.invalid === 'true') {
            this.$refs.mount.querySelectorAll('.ant-select-dropdown button').forEach((button) => setAttribute(button, 'tabindex', '-1'));

            const input = this.$refs.mount.querySelector(COMBOBOX);
            if (! input) return;

            setAttribute(input, 'aria-labelledby', [ids.label, ids.selection].filter(Boolean).join(' '));
            setAttribute(input, 'aria-describedby', ids.describedBy);

            if (invalid) setAttribute(input, 'aria-invalid', 'true');
            else if (input.getAttribute('aria-invalid') !== null) input.removeAttribute('aria-invalid');
        },

        sf_receive(dataMask, change) {
            // The component echoing the value it was given: nothing new, and writing it back could
            // undo a newer pick on a slow round trip.
            if (change?.auto) return;

            const value = fromDataMask(dataMask);
            sent = value;
            hasSent = true;
            this.sf_value = value;

            // After React's event: the combobox is told the value it now holds, so its next echo of
            // it is not taken for the reader's.
            queueMicrotask(() => {
                if (alive && bundle) this.sf_mount(value);
            });
        },

        sf_follow(value) {
            const own = hasSent && sameSelection(value, sent);
            hasSent = false;
            sent = undefined;
            if (own || ! bundle || (mounted && sameSelection(toSelection(value).value, mountedFor))) return;

            this.sf_mount(value);
        },
    };
}
